// src/shared/hooks/useNavigationItems.ts

import { useMemo } from 'react';
import { NavigationItem } from '../components/ServiceSidebar';
import { ROUTES } from '../constants/routes';

export interface User {
  id: number;
  username: string;
  name?: string;
  email?: string;
  role: string;
  department?: string | null;
  services?: string[];
}

// Define role-based navigation configurations
const ROLE_NAVIGATION_CONFIG = {
  // Employee level - basic access
  employee: [
    {
      path: ROUTES.QUALITY_MANAGEMENT.TEAM_CALENDAR,
      label: 'Calendário',
      icon: '📅',
      description: 'View team calendar and add tasks'
    },
    {
      path: ROUTES.QUALITY_MANAGEMENT.EMPLOYEE_DASHBOARD,
      label: 'Minhas Tarefas',
      icon: '👤',
      description: 'View your projects and tasks'
    },
    {
      path: ROUTES.QUALITY_MANAGEMENT.ANALYTICS_DASHBOARD,
      label: 'Analytics',
      icon: '📈',
      description: 'View analytics and metrics'
    }
  ],

  // Supervisor level - employee + overtime management
  supervisor: [
    {
      path: ROUTES.QUALITY_MANAGEMENT.TEAM_CALENDAR,
      label: 'Calendário',
      icon: '📅',
      description: 'View team calendar and add tasks'
    },
    {
      path: ROUTES.QUALITY_MANAGEMENT.EMPLOYEE_DASHBOARD,
      label: 'Minhas Tarefas',
      icon: '👤',
      description: 'View your projects and tasks'
    },
    {
      path: ROUTES.QUALITY_MANAGEMENT.OVERTIME_APPROVALS,
      label: 'Aprovações de Horas',
      icon: '⏰',
      description: 'Review pending overtime requests'
    },
    {
      path: ROUTES.QUALITY_MANAGEMENT.DELIVERY_PLAN,
      label: 'Plano de Entregas',
      icon: '📋',
      description: 'Timeline das tarefas por projeto e colaborador'
    },
    {
      path: ROUTES.QUALITY_MANAGEMENT.ANALYTICS_DASHBOARD,
      label: 'Analytics',
      icon: '📈',
      description: 'View analytics and metrics'
    }
  ],

  // Manager level - supervisor + project management
  manager: [
    {
      path: ROUTES.QUALITY_MANAGEMENT.TEAM_CALENDAR,
      label: 'Calendário',
      icon: '📅',
      description: 'View team calendar and add tasks'
    },
    {
      path: ROUTES.QUALITY_MANAGEMENT.EMPLOYEE_DASHBOARD,
      label: 'Minhas Tarefas',
      icon: '👤',
      description: 'View your projects and tasks'
    },
    {
      path: ROUTES.QUALITY_MANAGEMENT.OVERTIME_APPROVALS,
      label: 'Aprovações de Horas',
      icon: '⏰',
      description: 'Review pending overtime requests'
    },
    {
      path: ROUTES.QUALITY_MANAGEMENT.DELIVERY_PLAN,
      label: 'Plano de Entregas',
      icon: '📋',
      description: 'Timeline das tarefas por projeto e colaborador'
    },
    {
      path: ROUTES.QUALITY_MANAGEMENT.ANALYTICS_DASHBOARD,
      label: 'Analytics',
      icon: '📈',
      description: 'View project metrics and reports'
    }
  ],

  // Director level - manager + enhanced project management
  director: [
    {
      path: ROUTES.QUALITY_MANAGEMENT.TEAM_CALENDAR,
      label: 'Calendário',
      icon: '📅',
      description: 'View team calendar and project overview'
    },
    {
      path: ROUTES.QUALITY_MANAGEMENT.EMPLOYEE_DASHBOARD,
      label: 'Minhas Tarefas',
      icon: '👤',
      description: 'View your projects and tasks'
    },
    {
      path: ROUTES.QUALITY_MANAGEMENT.OVERTIME_APPROVALS,
      label: 'Aprovações de Horas',
      icon: '⏰',
      description: 'Review pending overtime requests'
    },
    {
      path: ROUTES.QUALITY_MANAGEMENT.DELIVERY_PLAN,
      label: 'Plano de Entregas',
      icon: '📋',
      description: 'Timeline das tarefas por projeto e colaborador'
    },
    {
      path: ROUTES.QUALITY_MANAGEMENT.ANALYTICS_DASHBOARD,
      label: 'Analytics',
      icon: '📈',
      description: 'View project metrics and reports'
    }
  ],

  // Admin level - full access
  admin: [
    {
      path: ROUTES.QUALITY_MANAGEMENT.TEAM_CALENDAR,
      label: 'Calendário',
      icon: '📅',
      description: 'View team calendar and add tasks'
    },
    {
      path: ROUTES.QUALITY_MANAGEMENT.EMPLOYEE_DASHBOARD,
      label: 'Minhas Tarefas',
      icon: '👤',
      description: 'View your projects and tasks'
    },
    {
      path: ROUTES.QUALITY_MANAGEMENT.OVERTIME_APPROVALS,
      label: 'Aprovações de Horas',
      icon: '⏰',
      description: 'Review pending overtime requests'
    },
    {
      path: ROUTES.QUALITY_MANAGEMENT.DELIVERY_PLAN,
      label: 'Plano de Entregas',
      icon: '📋',
      description: 'Timeline das tarefas por projeto e colaborador'
    },
    {
      path: ROUTES.QUALITY_MANAGEMENT.ANALYTICS_DASHBOARD,
      label: 'Analytics',
      icon: '📈',
      description: 'View comprehensive analytics'
    },
    {
      path: ROUTES.QUALITY_MANAGEMENT.ADMIN,
      label: 'Administração',
      icon: '⚙️',
      description: 'Manage users, roles and departments'
    }
  ]
};

// Helper function to normalize role for comparison
const normalizeRole = (role: string): string => {
  if (!role) return 'employee';
  
  const normalizedRole = role.toLowerCase().trim();
  
  // Handle various role formats
  if (normalizedRole.includes('admin')) return 'admin';
  if (normalizedRole.includes('director')) return 'director';
  if (normalizedRole.includes('manager')) return 'manager';
  if (normalizedRole.includes('supervisor')) return 'supervisor';
  
  // Specific role mappings
  if (normalizedRole === 'project manager') return 'manager';
  
  // Default to employee
  return 'employee';
};

// Hook to get navigation items based on user role
export const useNavigationItems = (user: User | null): NavigationItem[] => {
  return useMemo(() => {
    if (!user || !user.role) {
      return ROLE_NAVIGATION_CONFIG.employee;
    }

    const normalizedRole = normalizeRole(user.role);
    const navigationItems = ROLE_NAVIGATION_CONFIG[normalizedRole as keyof typeof ROLE_NAVIGATION_CONFIG];

    return navigationItems || ROLE_NAVIGATION_CONFIG.employee;
  }, [user?.role, user?.username]);
};

// Hook to check if a user can access a specific route
export const useCanAccessRoute = (user: User | null, route: string): boolean => {
  const navigationItems = useNavigationItems(user);
  return navigationItems.some(item => item.path === route);
};

export default useNavigationItems;